import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Search, Calendar, Trophy, AlertCircle, Wallet, NotebookPen, Check } from 'lucide-react'
import { usePicks, useParlays } from '@/hooks/usePicks'
import { useMyBets, useUpdateBetNotes, type PersonalBet } from '@/hooks/usePersonalBankroll'
import { useHome } from '@/hooks/useHome'
import { SecondaryPickCard } from '@/components/SecondaryPickCard'
import { TopPickCard } from '@/components/TopPickCard'
import { ParlayCard } from '@/components/ParlayCard'
import { SameGameParlayCard } from '@/components/SameGameParlayCard'
import { PickDetailSheet } from '@/components/PickDetailSheet'
import { SkeletonCard } from '@/components/LoadingSkeleton'
import { SkeletonList } from '@/components/LoadingSkeleton'
import { EmptyState } from '@/components/EmptyState'
import { PillTabs } from '@/components/PillTabs'
import { MyTickets } from '@/components/quiniela/MyTickets'
import { useQuinielaTickets } from '@/hooks/useQuiniela'
import { ErrorState } from '@/components/ErrorState'
import { RunBadge } from '@/components/RunBadge'
import { StatusBadge } from '@/components/StatusBadge'
import { formatOdds, cn } from '@/lib/utils'
import type { PickLeg } from '@/types/miniApp'
import type { Parlay } from '@/types/miniApp'

// Generar una lista de las últimas 14 fechas en formato YYYY-MM-DD
function getRecentDates(): { dateStr: string; label: string }[] {
  const dates = []
  const today = new Date()
  for (let i = 0; i < 14; i++) {
    const d = new Date()
    d.setDate(today.getDate() - i)
    const dateStr = d.toISOString().split('T')[0]
    const label = d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
    const weekday = i === 0 ? 'Hoy' : i === 1 ? 'Ayer' : d.toLocaleDateString('es-ES', { weekday: 'short' })
    dates.push({ dateStr, label: `${weekday} ${label}` })
  }
  return dates
}

// P0.26.1 (Sección E del encargo): categorías reales filtrables -- "Reto John Anthony"
// vive en su propia pantalla dedicada (`/reto`, con su propio historial semanal ya
// añadido en P0.26) y no se mezcla aquí (formato de dato distinto: pasos semanales, no
// picks/parlays). Marcadores Probables NUNCA aparece -- no es apuesta ni historial
// financiero (Sección E, explícito).
type Category = 'todos' | 'top_ia' | 'top_seguro' | 'secundarios' | 'conservador' | 'balanceado' | 'agresivo' | 'sgp'

// Sprint "Parlay Official Final" (25/09/2026): etiquetas renombradas a JA Duo/Triple/Quad --
// las claves internas no cambian (continuidad con `parlay_type`).
const CATEGORY_LABELS: Record<Category, string> = {
  todos: 'Todos',
  top_ia: 'Top 3 IA',
  top_seguro: 'Top 3 Seguro',
  secundarios: 'Secundarios',
  conservador: 'JA Duo',
  balanceado: 'JA Triple',
  agresivo: 'JA Quad',
  sgp: 'Same Game Parlay',
}

const PARLAY_TYPE_TO_CATEGORY: Record<string, Category> = {
  conservador: 'conservador',
  balanceado: 'balanceado',
  agresivo: 'agresivo',
  same_game_parlay: 'sgp',
}

/** Pestaña "Recomendaciones" -- histórico OFICIAL de John Anthony: qué recomendó el sistema
 * y qué resultado tuvo. Fuente: `/picks` + `/parlays` (DecisionRecord/Parlay), NUNCA mezclado
 * con lo que el cliente decidió jugar de verdad (esa es la otra pestaña). */
function RecommendationsTab() {
  const recentDates = getRecentDates()
  const [selectedDate, setSelectedDate] = useState(recentDates[0].dateStr)
  const [search, setSearch] = useState('')
  // P0.26 (Sección 14 del encargo): "Void" agregado como estado filtrable propio -- antes
  // quedaba fuera de los 4 filtros existentes, sin forma de aislarlo.
  const [statusFilter, setStatusFilter] = useState<'todos' | 'ganada' | 'perdida' | 'pendiente' | 'void'>('todos')
  const [category, setCategory] = useState<Category>('todos')
  const [selectedPick, setSelectedPick] = useState<PickLeg | null>(null)

  const { data, isLoading, isError } = usePicks(true, selectedDate)
  const { data: parlaysData, isLoading: parlaysLoading, isError: parlaysError } = useParlays(true, selectedDate)

  const taggedPicks: (PickLeg & { _category: Category })[] = data
    ? [
        ...(data.featured || []).map((p) => ({ ...p, _category: 'top_ia' as Category })),
        ...(data.safest_top_picks || []).map((p) => ({ ...p, _category: 'top_seguro' as Category })),
        ...(data.secondary || []).map((p) => ({ ...p, _category: 'secundarios' as Category })),
      ]
    : []

  const matchesCommon = (searchable: string, settledStatus: string | null | undefined) => {
    const matchesSearch = searchable.toLowerCase().includes(search.toLowerCase())
    const matchesStatus =
      statusFilter === 'todos' ||
      (statusFilter === 'pendiente' && !settledStatus) ||
      settledStatus === statusFilter
    return matchesSearch && matchesStatus
  }

  const filteredPicks = taggedPicks.filter((pick) => {
    if (category !== 'todos' && category !== pick._category) return false
    const searchable = [pick.home_team, pick.away_team, pick.league, pick.selection_label || pick.selection]
      .filter(Boolean)
      .join(' ')
    return matchesCommon(searchable, pick.settled_status)
  })

  const parlayCategoryVisible = category === 'todos' || ['conservador', 'balanceado', 'agresivo', 'sgp'].includes(category)
  const filteredParlays: Parlay[] = parlayCategoryVisible
    ? (parlaysData?.parlays || []).filter((parlay) => {
        const parlayCategory = PARLAY_TYPE_TO_CATEGORY[parlay.parlay_type]
        if (category !== 'todos' && category !== parlayCategory) return false
        const searchable = (parlay.legs || [])
          .map((l) => [l.home_team, l.away_team, l.league].filter(Boolean).join(' '))
          .join(' ')
        return matchesCommon(searchable, parlay.status as string | null | undefined)
      })
    : []

  const nothingFound = filteredPicks.length === 0 && filteredParlays.length === 0

  return (
    <div className="space-y-4">
      {/* Selector de Fecha Horizontal */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-text-dim pl-1">
          <Calendar className="h-3.5 w-3.5" />
          <span>Seleccionar Fecha</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none snap-x">
          {recentDates.map((item) => {
            const isSelected = selectedDate === item.dateStr
            return (
              <button
                key={item.dateStr}
                onClick={() => setSelectedDate(item.dateStr)}
                className={`snap-center shrink-0 px-3.5 py-2 rounded-xl text-xs font-bold border transition ${
                  isSelected
                    ? 'border-brand text-black bg-brand'
                    : 'border-border bg-panel text-text-muted hover:border-text-dim/40'
                }`}
              >
                {item.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Controles de Búsqueda y Filtros */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-dim" />
          <input
            type="text"
            placeholder="Buscar por equipo, liga o selección..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-panel border border-border rounded-xl pl-9 pr-4 py-2.5 text-xs text-text placeholder-text-dim focus:outline-none focus:border-brand/40 transition"
          />
        </div>

        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          {(Object.keys(CATEGORY_LABELS) as Category[]).map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`shrink-0 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border transition ${
                category === cat
                  ? 'border-brand text-black bg-brand'
                  : 'border-border bg-panel text-text-dim hover:border-text-dim/40'
              }`}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>

        <div className="flex gap-1.5 bg-panel-elevated/40 border border-border p-1 rounded-xl">
          {(['todos', 'ganada', 'perdida', 'pendiente', 'void'] as const).map((filter) => {
            const isSelected = statusFilter === filter
            const labels = {
              todos: 'Todos',
              ganada: 'Acertados',
              perdida: 'Fallados',
              pendiente: 'Pendientes',
              void: 'Void',
            }
            return (
              <button
                key={filter}
                onClick={() => setStatusFilter(filter)}
                className={`flex-1 text-center py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition ${
                  isSelected ? 'bg-brand text-black font-extrabold' : 'text-text-dim hover:text-text'
                }`}
              >
                {labels[filter]}
              </button>
            )
          })}
        </div>
      </div>

      <div className="space-y-4">
        {(isLoading || parlaysLoading) && (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {(isError || parlaysError) && (
          <div className="card border-danger/30 bg-danger-soft/10 text-center py-6 space-y-2">
            <AlertCircle className="h-8 w-8 text-danger mx-auto" />
            <p className="text-xs text-text-muted">Error al cargar el historial de esta fecha.</p>
          </div>
        )}

        {!isLoading && !parlaysLoading && !isError && !parlaysError && nothingFound && (
          <div className="card border-border bg-panel-elevated/20 text-center py-10 text-text-dim space-y-2">
            <Trophy className="h-8 w-8 text-text-dim/60 mx-auto" />
            <p className="text-xs font-bold uppercase tracking-wide">No se encontraron resultados</p>
            <p className="text-[11px] max-w-xs mx-auto">
              Prueba cambiando la fecha, la categoría o el término de búsqueda.
            </p>
          </div>
        )}

        {!isLoading && !isError && filteredPicks.length > 0 && (
          <div className="space-y-3">
            {filteredPicks.map((pick, i) =>
              pick._category === 'secundarios' ? (
                <SecondaryPickCard key={pick.decision_record_id} pick={pick} onSelect={() => setSelectedPick(pick)} />
              ) : (
                <TopPickCard
                  key={pick.decision_record_id}
                  pick={pick}
                  rank={pick.rank || i + 1}
                  onSelect={() => setSelectedPick(pick)}
                />
              )
            )}
          </div>
        )}

        {!parlaysLoading && !parlaysError && filteredParlays.length > 0 && (
          <div className="space-y-3">
            {filteredParlays.map((parlay) =>
              parlay.parlay_type === 'same_game_parlay' ? (
                <SameGameParlayCard key={parlay.id} parlay={parlay} />
              ) : (
                <ParlayCard key={parlay.id} parlay={parlay} />
              )
            )}
          </div>
        )}
      </div>

      {selectedPick && <PickDetailSheet pick={selectedPick} onClose={() => setSelectedPick(null)} />}
    </div>
  )
}

const BET_CATEGORY_LABEL: Record<string, string> = {
  top_ia: 'Top IA',
  safest: 'Más Seguros',
  parlay_conservador: 'JA Duo',
  parlay_balanceado: 'JA Triple',
  parlay_agresivo: 'JA Quad',
  exact_score: 'Resultado Exacto',
  challenge: 'Reto John Anthony',
}

const WINDOWS = ['7D', '30D', '90D', 'TOTAL'] as const

function formatMoney(value: number | undefined | null): string {
  if (value == null) return 'N/D'
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value)
}

/** Pestaña "Mis apuestas" -- EXCLUSIVAMENTE `PersonalBet` del usuario autenticado (lo que
 * decidió jugar de verdad, marcando "LA JUGUÉ"). P0.53 (Sección 11/13/18 del encargo): "Mi
 * cuota" (`played_odds`, la real conseguida) es el dato PRINCIPAL aquí -- la cuota John
 * Anthony (`reference_odds`, inmutable) queda como contexto secundario. */
/** Decision Journal BLACK (Sección 18/19 del encargo P0.55) -- nota opcional sobre una
 * apuesta propia. Nunca obligatoria, nunca visible/editable para otro plan (el backend ya la
 * rechaza con 403; aquí solo se oculta la UI si el plan no la incluye, coherente con el resto
 * de la app). */
function BetNoteEditor({ bet, hasDecisionJournal }: { bet: PersonalBet; hasDecisionJournal: boolean }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(bet.notes ?? '')
  const update = useUpdateBetNotes()

  if (!hasDecisionJournal) return null

  if (!editing && !bet.notes) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex items-center gap-1 text-[10px] text-text-dim hover:text-exact transition"
      >
        <NotebookPen className="h-3 w-3" /> Añadir nota
      </button>
    )
  }

  if (!editing && bet.notes) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex items-start gap-1.5 text-left text-[10px] text-text-dim bg-panel-elevated/40 border border-border/40 rounded-lg p-2 w-full hover:border-exact/30 transition"
      >
        <NotebookPen className="h-3 w-3 shrink-0 mt-0.5" />
        <span className="italic">{bet.notes}</span>
      </button>
    )
  }

  return (
    <div className="space-y-1.5">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder='Ej: "Entré tarde", "Bajé el stake"... (opcional)'
        maxLength={500}
        rows={2}
        className="w-full bg-panel-elevated border border-border/60 rounded-lg px-2.5 py-2 text-[11px] text-text placeholder-text-dim/60 focus:outline-none focus:border-exact/40 resize-none"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => { setEditing(false); setDraft(bet.notes ?? '') }}
          className="text-[10px] text-text-dim px-2 py-1"
        >
          Cancelar
        </button>
        <button
          onClick={() => update.mutate({ betId: bet.id, notes: draft.trim() || null }, { onSuccess: () => setEditing(false) })}
          disabled={update.isPending}
          className="flex items-center gap-1 text-[10px] font-bold text-black bg-brand px-2.5 py-1 rounded-lg disabled:opacity-50"
        >
          <Check className="h-3 w-3" /> Guardar
        </button>
      </div>
    </div>
  )
}

function MyBetsTab() {
  const [window, setWindowFilter] = useState<(typeof WINDOWS)[number]>('TOTAL')
  const { data: myBets, isLoading, isError, error, refetch } = useMyBets(window)
  const { data: homeData } = useHome(true)
  const hasDecisionJournal = Boolean(homeData?.plan_features?.decision_journal)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-1 bg-panel p-1 rounded-xl border border-border">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setWindowFilter(w)}
              className={cn(
                'px-2 py-1 rounded-lg text-[9px] font-black uppercase transition',
                window === w ? 'bg-brand text-black' : 'text-text-dim'
              )}
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <SkeletonList count={2} />}

      {/* P0.56 (Sección 18 del encargo): un fallo real de la API nunca debe dejar la
          pantalla en blanco -- antes esta pestaña no manejaba isError y simplemente no
          renderizaba nada. */}
      {isError && <ErrorState error={error} onRetry={() => refetch()} />}

      {myBets && myBets.items.length === 0 && (
        <EmptyState icon={Wallet} title="Sin apuestas registradas" body='Marca "LA JUGUÉ" en cualquier recomendación oficial para verla aquí.' />
      )}

      {myBets?.items.map((bet) => (
        <div key={bet.id} className="card p-3.5 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-black uppercase text-text-dim bg-panel-elevated px-2 py-0.5 rounded border border-border">
                {BET_CATEGORY_LABEL[bet.category] ?? bet.category}
              </span>
              {bet.run_type && <RunBadge runType={bet.run_type} compact />}
            </div>
            <StatusBadge status={bet.status === 'PENDING' ? 'pendiente' : bet.status === 'WON' ? 'ganada' : bet.status === 'LOST' ? 'perdida' : 'void'} />
          </div>
          {bet.match_label && <p className="text-[11px] font-bold text-text-dim">{bet.match_label}</p>}
          <p className="text-xs font-bold text-text">{bet.market_label} — {bet.selection_label}</p>
          <p className="text-[10px] text-text-dim">{new Date(`${bet.bet_date}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</p>
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-mono font-bold text-text">
              Mi cuota @{formatOdds(bet.played_odds)}
              <span className="text-[10px] font-normal text-text-dim"> · Cuota JA @{formatOdds(bet.reference_odds)}</span>
            </span>
            <span className="font-mono font-bold text-text">Stake {formatMoney(bet.stake)}</span>
          </div>
          {bet.profit_loss != null && (
            <p className={cn('text-xs font-mono font-black text-right', bet.profit_loss >= 0 ? 'text-success' : 'text-danger')}>
              {bet.profit_loss >= 0 ? '+' : ''}{formatMoney(bet.profit_loss)}
            </p>
          )}
          <BetNoteEditor bet={bet} hasDecisionJournal={hasDecisionJournal} />
        </div>
      ))}
    </div>
  )
}

/** "Mis apuestas": apuestas normales (`PersonalBet`) y, SEPARADO, "Mis boletos" de La Quiniela (tabla propia, con
 * aciertos y liquidación propios). Nunca se mezclan ni cuentan en el ROI de las apuestas normales. */
function MyBetsSection() {
  const [searchParams, setSearchParams] = useSearchParams()
  const sub = searchParams.get('sub') === 'boletos' ? 'boletos' : 'apuestas'
  const { data: tickets } = useQuinielaTickets(true)

  function selectSub(next: string) {
    const params: Record<string, string> = { tab: 'apuestas' }
    if (next === 'boletos') params.sub = 'boletos'
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="space-y-4">
      <PillTabs
        items={[
          { key: 'apuestas', label: 'Apuestas' },
          { key: 'boletos', label: 'Mis boletos', count: tickets?.summary.total },
        ]}
        active={sub} onChange={selectSub}
      />
      {sub === 'boletos' ? <MyTickets /> : <MyBetsTab />}
    </div>
  )
}

/** P0.53 (29/08/2026, Sección 11/12 del encargo): "Historial" unificado -- dos pestañas,
 * dos fuentes de datos que NUNCA se mezclan: "Recomendaciones" (histórico oficial de John
 * Anthony) y "Mis apuestas" (`PersonalBet`, lo que el cliente jugó de verdad). Antes eran dos
 * pantallas en dos sitios distintos de la navegación -- ahora una sola, con la diferencia
 * explícita en la propia UI (nunca implícita). */
export function HistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') === 'apuestas' ? 'apuestas' : 'recomendaciones'
  const [tab, setTab] = useState<'recomendaciones' | 'apuestas'>(initialTab)

  function selectTab(next: 'recomendaciones' | 'apuestas') {
    setTab(next)
    setSearchParams(next === 'apuestas' ? { tab: 'apuestas' } : {}, { replace: true })
  }

  return (
    <div className="p-4 space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/mas" className="p-2 rounded-xl bg-panel-elevated hover:bg-border text-text transition">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-lg font-bold text-text">Historial</h1>
          <p className="text-xs text-text-dim">Qué recomendó John Anthony y qué jugaste tú -- nunca es lo mismo.</p>
        </div>
      </div>

      <div className="flex gap-1.5 bg-panel-elevated/40 border border-border p-1 rounded-xl">
        <button
          onClick={() => selectTab('recomendaciones')}
          className={cn(
            'flex-1 text-center py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition',
            tab === 'recomendaciones' ? 'bg-brand text-black' : 'text-text-dim hover:text-text'
          )}
        >
          Recomendaciones
        </button>
        <button
          onClick={() => selectTab('apuestas')}
          className={cn(
            'flex-1 text-center py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition',
            tab === 'apuestas' ? 'bg-brand text-black' : 'text-text-dim hover:text-text'
          )}
        >
          Mis apuestas
        </button>
      </div>

      {tab === 'recomendaciones' ? <RecommendationsTab /> : <MyBetsSection />}
    </div>
  )
}
